/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, { Fragment } from 'react';
import { RouteComponentProps } from '@reach/router';
import { ProceedOrCancelButton } from '../../../../pge-plus-common/StepperForm';
import routes from '../../../../../routes';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import useFormState from '../../../../../hooks/useFormState';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import { useTheme } from '@material-ui/core';
import colors from '../../../../../themes/main-colors';
import { ContactInfoBox } from '../../../../pge-plus-form/ContactInfo/ContactInfoBox';
import RadioButtonGroup from '../../../../pge-plus-common/RadioButtonGroup';
import TextField from '../../../../text-field';
import PhoneTextField from '../../../../phone-text-field';
import EmailTextField from '../../../../email-text-field';
import { DropdownMenu } from '../../../../pge-plus-common/DropdownMenu';
import PopoverToolTip from '../../../../pge-plus-common/PopoverToolTip';
import { homeTypes } from '../../../../pge-plus-form/ContactInfo/homeTypes';
import CommonButton from '../../../../pge-plus-common/CommonButton';
import { createAccountInfoValidateFunction } from '../rebateOnlyForm.rules';
import {
  ApplicationErSectionType,
  ApplicationSectionStatus,
  ErEnrollmentPreference,
  HomeType,
  SectionErAccountInfo,
} from '../../../../../__generated__/pgeplus-types';
import { navigate } from 'gatsby';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import useUtil from '../../../../../hooks/pge-plus/useUtil';
import useUpdateERSections from '../../../../../hooks/pge-plus/useUpdateERSections';
import { accountInfoERFormStateAdapter } from '../rebateOnlyFormState.adapter';
import {
  AccountInfo,
  ChargerType,
  UpdateSectionErrorCode,
} from '../rebateOnlyForm.types';
import { SaveAndComeBackLink } from '../../../../pge-plus-common/SaveAndComeBackLink';

const CustomerInformation: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const {
    formState: { accountInfo, application, rebateInfo },
    getSectionData,
    updateFormState,
  } = useRebateOnlyFormState();

  const sectionDataAccountInfo: SectionErAccountInfo = getSectionData(
    ApplicationErSectionType.ErAccountInfo,
  );

  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CUSTOMER_INFORMATION,
  );

  const form = useFormState(accountInfo, {
    validate: createAccountInfoValidateFunction(),
    validationContext: {
      content,
    },
  });
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();
  const { updateSectionAccountInfo } = useUpdateERSections();

  const theme = useTheme();

  const onSubmit = wrapWithLoader(async (data: AccountInfo) => {
    try {
      const response = await updateSectionAccountInfo(
        accountInfoERFormStateAdapter().fromFormState(
          data,
          application?.id!,
          sectionDataAccountInfo?.id,
          ApplicationSectionStatus.Completed,
        ),
      );

      // Save the response in a local var
      const result = response?.data?.updateSectionERAccountInfo;

      // Check for errors to show the red banner and block navigation
      if (response?.errors || result?.validation?.errors) {
        throw new Error();
      }

      //Update the app state with the user selected values
      if (result?.section?.data) {
        const responseData = result?.section?.data;
        const updatedValues = accountInfoERFormStateAdapter().toFormState(
          responseData,
        );

        updateFormState('accountInfo', updatedValues);
      }

      // Check for section errors to navigate to a specific fiddlesticks page based on the error code
      if (result?.section.errors.length) {
        const ineligibleHomeTypeFound =
          result?.section.errors.filter(
            error => error.code === UpdateSectionErrorCode.IneligibleHomeType,
          ).length > 0;
        if (ineligibleHomeTypeFound) {
          void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
            state: {
              message: 'REBATE_ONLY_MULTIFAMILY_INELIGIBLITY',
            },
          });
          return;
        }
      }

      // If there are no GQL, Validation errors or Section errors then proceed with the onSubmit logic
      if (result?.section.errors.length === 0 && result?.section.data) {
        // CUP-24444: All paths (charger/vehicle/both-qualifief) go thru the eligibility check page now. 
        void navigate(routes.PGE_PLUS_REBATE_ONLY_HIGHER_REBATES_CHECK);
      } else {
        throw new Error();
      }
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const onPhoneNumberUpdate = (phone: string) => {
    updateFormState('application', {
      ...application,
      //@ts-ignore
      details: application?.details.map(details => {
        if (details.sectionType === ApplicationErSectionType.ErAccountInfo) {
          const accInfo = details as SectionErAccountInfo;
          if (accInfo.data.accountContact) {
            return {
              ...details,
              data: {
                ...accInfo.data,
                accountContact: {
                  ...accInfo.data.accountContact,
                  phoneNumber: phone,
                },
              },
            };
          } else {
            return details;
          }
        }
        return details;
      }),
    });
  };

  const relationshipOptions = [
    { label: content.get('RELATIONSHIP_PARENT'), value: 'parent' },
    { label: content.get('RELATIONSHIP_SIBLING'), value: 'sibling' },
    { label: content.get('RELATIONSHIP_SPOUSE'), value: 'spouse' },
    { label: content.get('RELATIONSHIP_PARTNER'), value: 'partner' },
    { label: content.get('RELATIONSHIP_FRIEND'), value: 'friend' },
    { label: content.get('RELATIONSHIP_OTHER'), value: 'other' },
  ];

  // Styles
  const subText1 = {
    fontSize: 20,
    marginBottom: theme.spacing(4),
  };
  const boldText = {
    fontWeight: 700,
  };

  const divider = {
    color: colors.lightGray1,
  };

  const subTitleHolder = {
    fontWeight: 700,
    fontFamily: 'Untitled-Sans',
    margin: theme.spacing(0, 0, 3, 0),
    [`@media (min-width: ${theme.breakpoints.values.sm}px)`]: {
      fontSize: '20px',
    },
  };

  const mainContactFields = {
    width: '100%',
    margin: '8px 0 8px 0',
  };

  const userInfo = {
    firstName: sectionDataAccountInfo?.data.accountContact.firstName,
    middleName: sectionDataAccountInfo?.data?.accountContact?.middleName,
    lastName: sectionDataAccountInfo?.data?.accountContact?.lastName,
    address: sectionDataAccountInfo?.data?.address?.addressLine1,
    phoneNumber:
      sectionDataAccountInfo?.data?.accountContact?.phoneNumber || '',
    emailAddress: sectionDataAccountInfo?.data?.accountContact?.emailAddress,
  };

  const handleSaveAndExit = wrapWithLoader(async () => {
    try {
      const response = await updateSectionAccountInfo({
        ...accountInfoERFormStateAdapter().fromFormState(
          form.values,
          application?.id!,
          sectionDataAccountInfo?.id,
        ),
        saveAndComeBack: true,
      });
      if (
        response?.errors ||
        response.data?.updateSectionERAccountInfo?.validation?.errors ||
        response.data?.updateSectionERAccountInfo?.section?.errors.length !== 0
      ) {
        throw new Error();
      } else {
        void navigate(routes.ACCOUNT);
      }
    } catch {
      setErrorNotification(true);
    }
  });

  return (
    <Fragment>
      <form
        onSubmit={form.submit(values => onSubmit(values), console.log)}
        noValidate
      >
        {/* Generic Description */}
        <Box>
          <Typography css={{ ...subText1 }}>
            {content.get('REBATE_ONLY_CUSTOMER_INFO_1')}
            <span
              css={{
                fontWeight: 'bold',
                '& a': {
                  textDecoration: 'none',
                },
              }}
            >
              {inlineRichText(content.get('REBATE_ONLY_CUSTOMER_INFO_2'))}
            </span>
            {content.get('REBATE_ONLY_CUSTOMER_INFO_3')}
          </Typography>

          <Typography css={{ ...subText1, ...boldText }}>
            {content.get('REBATE_ONLY_CONFIRM_YOUR_CONTACT_INFO')}
          </Typography>
        </Box>

        {/* Contact Info Section */}
        <ContactInfoBox
          userInfo={userInfo}
          content={content}
          onPhoneNumberUpdate={onPhoneNumberUpdate}
        />

        {/* Main Contact Section */}
        <Box
          css={{ marginTop: theme.spacing(3) }}
          data-testid="main-contact-section"
        >
          <Typography
            data-testid="mailing-address-title"
            css={{ ...subTitleHolder }}
            variant="h4"
          >
            {content.get('REBATE_ONLY_MAIN_POINT_OF_CONTACT')}
          </Typography>
          <Box display={'flex'} justifyContent={'center'}>
            <Grid xs={12} md={6}>
              <RadioButtonGroup
                options={[
                  {
                    label: content.get('REUSABLE_YES') || '',
                    value: true,
                  },
                  {
                    label: content.get('REUSABLE_NO') || '',
                    value: false,
                  },
                ]}
                state={form.values.isMainPerson}
                setState={async e => {
                  if (e) {
                    //Reset the user info
                    void form.setValue('firstName', '');
                    void form.setValue('lastName', '');
                    void form.setValue('emailAddress', '');
                    void form.setValue('phoneNumber', '');
                    void form.setValue('relationship', '');
                  }
                  await form.setValue('isMainPerson', e);
                }}
              />
            </Grid>
          </Box>
          {form.errors.isMainPerson && (
            <FormHelperText error>{form.errors.isMainPerson}</FormHelperText>
          )}
        </Box>

        {/* Main contact fields */}
        {form.values.isMainPerson === false && (
          <Box
            mt={2}
            css={{
              '& .MuiFormControl-root': {
                background: 'transparent',
              },
              '& .MuiInputBase-input': {
                background: '#FFF',
              },
            }}
          >
            <Typography
              variant="body2"
              css={{ marginTop: 30, marginBottom: 20, fontSize: '20px' }}
            >
              {content.get('REBATE_ONLY_POINT_OF_CONTACT_DETAILS')}
            </Typography>

            <Grid container direction="column">
              <Grid container item spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    margin={'normal'}
                    css={{ ...mainContactFields }}
                    name={'name'}
                    label={content.get('FIRST_NAME')}
                    {...form.props('firstName')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    margin={'normal'}
                    css={{ ...mainContactFields }}
                    name={'name'}
                    label={content.get('LAST_NAME')}
                    {...form.props('lastName')}
                  />
                </Grid>
              </Grid>
              <Grid container item spacing={2}>
                <Grid item xs={12} md={6}>
                  <PhoneTextField
                    css={{ ...mainContactFields }}
                    data-testid="primaryPhone"
                    label={content.get('REUSABLE_PHONE')}
                    name="phoneNumber"
                    {...form.props('phoneNumber')}
                    error={Boolean(form.errors.phoneNumber)}
                    helperText={form.errors.phoneNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <EmailTextField
                    css={{ ...mainContactFields }}
                    label={content.get('REUSABLE_EMAIL')}
                    name="email"
                    inputProps={{ minLength: 1, maxLength: 60 }}
                    {...form.props('emailAddress')}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Typography
              variant="body2"
              css={{
                fontSize: '14px',
                lineHeight: '20px !important',
                marginTop: 5,
                marginBottom: 20,
              }}
            >
              {content.get('CONTACT_DETAILS_DISCLAIMER')}
            </Typography>

            <Box mt={2}>
              <DropdownMenu
                name={content.get('RELATIONSHIP_TO_USER')}
                options={relationshipOptions}
                state={form.values.relationship || ''}
                setState={e => {
                  void form.setValue('relationship', e);
                }}
                errorMsg={(form.errors.relationship as string) || ''}
              />
            </Box>

            <Divider css={{ ...divider, margin: theme.spacing(4, 0) }} />
          </Box>
        )}

        <Box display={'flex'} css={{ marginTop: theme.spacing(3) }}>
          <Typography
            data-testid="mailing-address-title"
            css={{ ...subTitleHolder }}
            variant="h4"
          >
            {content.get('REBATE_ONLY_WHAT_KIND_OF_STRUCTURE')}
            <PopoverToolTip
              popoverContent={content.get('WHAT_KIND_OF_STRUCTURE_TOOLTIP')}
            />
          </Typography>
        </Box>

        {/* Type of property buttons section */}
        <Grid container spacing={2} css={{ justifyContent: 'space-between' }}>
          {homeTypes(content).map(home => (
            <Grid item md={4} xs={6}>
              <CommonButton
                buttonLabel={home.textLabel}
                handleOnClick={(e: React.MouseEvent<HTMLElement>) =>
                  form.setValue('homeType', home.formValue)
                }
                isActive={form.values.homeType === home.formValue}
                img={home.img}
              />
            </Grid>
          ))}
        </Grid>

        {form.errors.homeType && (
          <FormHelperText error>{form.errors.homeType}</FormHelperText>
        )}

        <Divider css={{ ...divider, margin: theme.spacing(3, 0) }} />

        <ProceedOrCancelButton
          back={
            rebateInfo.enrollmentPreference === ErEnrollmentPreference.UseMyVehicle
              ? routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN
              : rebateInfo.chargerValidation?.isQualifiedProduct &&
                rebateInfo.chargerValidation?.product?.brand !==
                  ChargerType.JUICE_BOX
              ? routes.PGE_PLUS_REBATE_ONLY_UPLOAD_SERIAL_NUMBER
              : rebateInfo.chargerValidation?.isQualifiedProduct &&
                rebateInfo.chargerValidation?.product?.brand ===
                  ChargerType.JUICE_BOX
              ? routes.PGE_PLUS_REBATE_ONLY_UPLOAD_JUICEBOXID_NUMBER
              : routes.PGE_PLUS_REBATE_ONLY_UPLOAD_VIN
          }
          disable={
            form.values.isMainPerson === undefined ||
            (form.values.isMainPerson && form.values.homeType === null) ||
            (!form.values.isMainPerson &&
              (!form.values.firstName ||
                !form.values.lastName ||
                !form.values.emailAddress ||
                !form.values.phoneNumber ||
                !form.values.relationship)) ||
            !form.values.homeType
          }
          backLabel={content.get('BACK')}
        />
      </form>
      <Box mt={6}>
        <SaveAndComeBackLink
          content={content}
          onSaveAndExit={handleSaveAndExit}
        />
      </Box>
    </Fragment>
  );
};

export default CustomerInformation;
